import { UserMinus, UserPlus, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { Field, Select } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { Tag } from '@/components/ui/Tag'
import { ApiError } from '@/lib/api'
import { describeProblem } from '@/lib/problems'
import {
  useAssignQuoteTeamMember,
  useQuoteTeam,
  useRemoveQuoteTeamMember,
} from '@/features/quotes/api/useQuote'
import { useColleagues } from '@/features/directories/api/useColleagues'
import type { QuoteDetail, QuoteTeamMember } from '@/types/domain'

/* The internal team on a SAVED quote (Story 2.5, FR-14, FR-69).
 *
 * ── Why this exists when QuoteTeamCard already did ───────────────────────────
 *
 * QuoteTeamCard is the WIZARD's card: it edits a local `QuoteDraft` that is
 * submitted with the create, because there is no quote id to hang a membership
 * on until the POST returns. This one edits a quote that exists, through
 * GET/POST/DELETE /api/quotes/:id/team.
 *
 * Until 2026-08-17 only the first existed, so a colleague could be named at
 * creation and never afterwards — and `useQuoteTeam`, `useAssignQuoteTeamMember`
 * and `useRemoveQuoteTeamMember` were written, correct, and called by nothing.
 * The whole of Story 2.5 is "assign a Pricing colleague to my quote"; the deal
 * this feature is for is the one where Sales realises on day three that they
 * need Yiwu's costing, which is exactly the moment the wizard is gone.
 *
 * Modelled on CaseTeamSection rather than reimplementing it. The two records
 * keep separate components because their copy differs — a case is running work
 * and a quote is not yet won, so "they can see this" means something different
 * on each, which is why the i18n catalogues keep `cases.team` and `quotes.team`
 * apart. What is shared is the SHAPE, and it is deliberately identical: FR-69
 * governs both sides, and two surfaces answering one requirement differently is
 * the drift this pass exists to remove.
 *
 * ── Membership is not decoration ─────────────────────────────────────────────
 *
 * Since migration 0028 `quote_team` IS the row-scope predicate (AD-5). Somebody
 * added here can open the quote from their next request; somebody removed loses
 * it just as fast (FR-56). That is why neither mutation is optimistic — a row
 * rendered before the server agreed would be a screen claiming a colleague can
 * see a quote they cannot — and why the two refusals below are the feature
 * rather than an edge case.
 */
export function QuoteTeamSection({ record }: { record: QuoteDetail }) {
  const { t } = useTranslation()
  const [adding, setAdding] = useState(false)

  const { data: team } = useQuoteTeam(record.id)

  const assign = useAssignQuoteTeamMember(record.id)
  const remove = useRemoveQuoteTeamMember(record.id)

  const members = team ?? []

  return (
    <Card>
      <CardHeader
        action={
          <Button
            variant="secondary"
            size="sm"
            iconLeft={UserPlus}
            onClick={() => {
              // Both reset, not just the one about to run: a stale refusal from
              // the other act would sit above a sheet that has nothing to do
              // with it.
              assign.reset()
              remove.reset()
              setAdding(true)
            }}
          >
            {t('quotes.team.add')}
          </Button>
        }
      >
        <CardTitle className="flex items-center gap-2">
          <Users size={15} className="text-text-muted" aria-hidden />
          {t('quotes.team.title')}
        </CardTitle>
      </CardHeader>

      <CardBody className="flex flex-col gap-1 pt-2">
        {/* Whichever write last failed is the one to report. There is no global
            mutation error handler in this client, so an unread `.error` is a
            silent refusal — the button appears to do nothing and the user
            presses it again. */}
        <TeamFailure assignError={assign.error} removeError={remove.error} />

        {members.length === 0 ? (
          <p className="text-text-muted text-[12.5px] leading-snug font-light">
            {t('quotes.team.empty')}
          </p>
        ) : null}

        {members.map((member) => (
          <TeamRow
            key={member.userId}
            member={member}
            pending={remove.isPending}
            onRemove={() => {
              assign.reset()
              remove.mutate(member.userId)
            }}
          />
        ))}
      </CardBody>

      <AssignSheet
        open={adding}
        onClose={() => setAdding(false)}
        team={members}
        pending={assign.isPending}
        onAssign={(userId) =>
          // No reason: this card STAFFS a quote. Asking for a price is its own
          // act on its own surface, and sending "pricing" from here would put a
          // task in somebody's bell every time a colleague was added to read.
          assign.mutate({ userId }, { onSuccess: () => setAdding(false) })
        }
      />
    </Card>
  )
}

/* The two refusals, in words the user can act on.
 *
 * KEYED ON WHICH ACT FAILED, not on the problem document, and that is forced
 * rather than lazy: the server answers both with `409 conflict`
 * (quote/handlers.go), distinguished only by an English `detail` — and AD-22
 * says the client maps the stable type suffix to a translated message rather
 * than displaying that developer-facing string. The suffix is the same for both.
 *
 * The act is enough to tell them apart, because each act can only meet one of
 * them: Assign refuses a deactivated account (FR-57) and Unassign refuses
 * emptying the team. Reading the English detail to choose between them would be
 * parsing prose that is explicitly not a contract; splitting the suffix would be
 * a change to the shared problem registry for a distinction only this surface
 * needs.
 *
 * Anything that is NOT a conflict — a dropped connection, a 500 — falls through
 * to the generic mapping, because "that account cannot be assigned" is a lie
 * about a network failure and sends the user to fix the wrong thing.
 */
function TeamFailure({
  assignError,
  removeError,
}: {
  assignError: Error | null
  removeError: Error | null
}) {
  const { t } = useTranslation()

  const failure = assignError ?? removeError
  if (!failure) return null

  const conflict = failure instanceof ApiError && failure.code === 'conflict'
  const message = conflict
    ? assignError
      ? t('quotes.team.notAssignable')
      : t('quotes.team.lastMember')
    : t(describeProblem(failure).detailKey)

  return (
    <p role="alert" className="text-attention pb-1 text-[12.5px] leading-snug">
      {message}
    </p>
  )
}

function TeamRow({
  member,
  pending,
  onRemove,
}: {
  member: QuoteTeamMember
  pending: boolean
  onRemove: () => void
}) {
  const { t } = useTranslation()

  return (
    <div className="border-line-hair flex items-center gap-2.5 border-b py-2 last:border-b-0">
      <span className="text-text-heading min-w-0 flex-1 truncate text-[13px]">
        {member.name}
      </span>
      <span className="text-text-muted text-[12px]">
        {t(`roles.${member.role}` as 'roles.sales', { defaultValue: member.role })}
      </span>
      {member.owner ? <Tag tone="neutral">{t('quotes.team.owner')}</Tag> : null}

      {/* Offered on every member INCLUDING the owner, which is CaseTeamSection's
          call and holds here for the same reason: the rule that protects a quote
          is "not the LAST one", not "not the owner". An owner who has handed a
          deal over should be able to step off it, and only a transaction can ask
          whether anybody would be left — so the refusal is the server's. */}
      <Button
        variant="ghost"
        size="sm"
        iconLeft={UserMinus}
        disabled={pending}
        aria-label={t('quotes.team.remove', { name: member.name })}
        onClick={onRemove}
      />
    </div>
  )
}

/* The picker.
 *
 * Deactivated accounts are absent from it (FR-57) — a COURTESY, not the control:
 * the server refuses the assignment regardless, because an account can be
 * deactivated between this list rendering and the click.
 *
 * The roster is GET /api/users through useColleagues, NOT /admin/users. That
 * endpoint is gated on admin.users.manage, so every non-founder who opened the
 * equivalent case sheet once got a 403 and an empty picker — and an empty picker
 * reads as "nobody to assign" rather than as a permission error, which is why it
 * went unnoticed for so long.
 */
function AssignSheet({
  open,
  onClose,
  team,
  pending,
  onAssign,
}: {
  open: boolean
  onClose: () => void
  team: QuoteTeamMember[]
  pending: boolean
  onAssign: (userId: string) => void
}) {
  const { t } = useTranslation()
  const { data: colleagues } = useColleagues()
  const [chosen, setChosen] = useState('')

  const assigned = new Set(team.map((member) => member.userId))
  // No isActive filter: the roster endpoint has already dropped accounts that
  // cannot sign in (FR-57), so a second check here would be a copy of a rule the
  // server owns — and a copy that drifts is a picker offering somebody the
  // transaction then refuses.
  const options = (colleagues ?? [])
    .filter((person) => !assigned.has(person.id))
    .map((person) => ({ value: person.id, label: person.name }))

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('quotes.team.add')}
      pending={pending}
      disabled={!chosen}
      onSubmit={() => onAssign(chosen)}
    >
      <Field label={t('quotes.team.colleague')} required>
        {(props) => (
          <Select
            value={chosen}
            onChange={(value) => setChosen(value)}
            options={options}
            placeholder={t('common.form.selectPlaceholder')}
            {...props}
          />
        )}
      </Field>
      {/* Distinguishes "nobody else works here" from "the list failed to load",
          which is the reading a bare empty dropdown invites. */}
      <p className="text-text-muted text-[12px] leading-snug font-light">
        {options.length === 0
          ? t('quotes.team.noneAvailable')
          : t('quotes.team.hint')}
      </p>
    </FormSheet>
  )
}
